import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/**
 * Cookie consent UI, PRESENTATION + STATE ONLY.
 *
 * This renders a GDPR/PECR-shaped banner and preferences panel and remembers the
 * visitor's choice. It does NOT yet gate any tags. Wire the real enforcement in
 * `applyConsent()` below (Google Consent Mode + gating the GA4 gtag load and the
 * CustomerLabs / Meta CAPI tags). Until that is wired, non-essential tags still
 * fire regardless of the choice shown here.
 *
 * Only wire this up on the site owner's explicit instruction (see
 * docs/ANALYTICS-CONTRACT.md, Consent / PECR).
 *
 * Compliance shape already handled here:
 *  - Analytics + Marketing default OFF (opt-in, nothing pre-ticked).
 *  - "Reject all" is exactly as prominent and easy as "Accept all".
 *  - Granular per-category control; Essential is clearly always-on.
 *  - Link to the full Cookie Policy; choice is re-openable to withdraw consent.
 */

const STORAGE_KEY = "cd_cookie_consent_v1";

export type ConsentPreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const DENIED: ConsentPreferences = { necessary: true, analytics: false, marketing: false };
const GRANTED: ConsentPreferences = { necessary: true, analytics: true, marketing: true };

/**
 * ── WIRE-UP SEAM (functional step, do later) ──────────────────────────────
 * Map the saved preferences onto real enforcement. Nothing analytics/marketing
 * should run until the matching flag is true. Suggested Consent Mode v2 call:
 *
 *   window.gtag?.("consent", "update", {
 *     analytics_storage:   prefs.analytics ? "granted" : "denied",
 *     ad_storage:          prefs.marketing ? "granted" : "denied",
 *     ad_user_data:        prefs.marketing ? "granted" : "denied",
 *     ad_personalization:  prefs.marketing ? "granted" : "denied",
 *   });
 *
 * Also gate the CustomerLabs / Meta tags on `prefs.marketing`.
 */
function applyConsent(_prefs: ConsentPreferences): void {
  // TODO(functional): connect to Consent Mode + GA4 + CustomerLabs gating.
}

/** Re-open the preferences panel (e.g. from a footer "Cookie settings" link). */
export function openCookiePreferences(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("cd:open-cookie-preferences"));
}

function readStored(): ConsentPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentPreferences) : null;
  } catch {
    return null;
  }
}

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Show on first visit (no stored choice). Always allow re-opening later.
  useEffect(() => {
    if (!readStored()) setOpen(true);
    const reopen = () => {
      const stored = readStored();
      setAnalytics(stored?.analytics ?? false);
      setMarketing(stored?.marketing ?? false);
      setShowDetail(true);
      setOpen(true);
    };
    window.addEventListener("cd:open-cookie-preferences", reopen);
    return () => window.removeEventListener("cd:open-cookie-preferences", reopen);
  }, []);

  const persist = useCallback((prefs: ConsentPreferences) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable, choice simply won't persist */
    }
    applyConsent(prefs);
    setOpen(false);
    setShowDetail(false);
  }, []);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto w-full max-w-xl rounded-2xl border border-border bg-card shadow-xl p-5 sm:p-6">
        {!showDetail ? (
          <>
            <h2 className="text-base font-bold text-foreground mb-1.5">We use cookies</h2>
            <p className="text-sm leading-relaxed text-muted-foreground mb-4">
              Some are needed to run the site. With your consent we also use cookies to
              measure how the site is used and how our ads perform. You can accept,
              reject, or choose. See our{" "}
              <Link to="/cookie-policy/" className="text-primary underline underline-offset-2">
                Cookie Policy
              </Link>
              .
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button variant="outline" className="flex-1" onClick={() => persist(DENIED)}>
                Reject all
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  const stored = readStored();
                  setAnalytics(stored?.analytics ?? false);
                  setMarketing(stored?.marketing ?? false);
                  setShowDetail(true);
                }}
              >
                Manage
              </Button>
              <Button variant="cta" className="flex-1" onClick={() => persist(GRANTED)}>
                Accept all
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-base font-bold text-foreground mb-3">Cookie preferences</h2>
            <div className="space-y-3">
              <label className="flex items-start justify-between gap-4 rounded-xl border border-border/60 bg-secondary/30 p-3">
                <span>
                  <span className="block text-sm font-semibold text-foreground">Essential</span>
                  <span className="block text-xs text-muted-foreground">
                    Always on. Needed for the site to work.
                  </span>
                </span>
                <Switch checked disabled aria-label="Essential cookies (always on)" />
              </label>

              <label className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
                <span>
                  <span className="block text-sm font-semibold text-foreground">Analytics</span>
                  <span className="block text-xs text-muted-foreground">
                    Helps us understand how the site is used (Google Analytics).
                  </span>
                </span>
                <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Analytics cookies" />
              </label>

              <label className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
                <span>
                  <span className="block text-sm font-semibold text-foreground">Marketing</span>
                  <span className="block text-xs text-muted-foreground">
                    Measures how our ads perform (Meta).
                  </span>
                </span>
                <Switch checked={marketing} onCheckedChange={setMarketing} aria-label="Marketing cookies" />
              </label>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button variant="ghost" className="sm:flex-none" onClick={() => setShowDetail(false)}>
                Back
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => persist(DENIED)}>
                Reject all
              </Button>
              <Button
                variant="cta"
                className="flex-1"
                onClick={() => persist({ necessary: true, analytics, marketing })}
              >
                Save preferences
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
